import React from "react";
import {
  UiSectionBody,
  UiSection,
  UiSectionHeader,
  UiContentStackTertiary,
  UiMediaSurface,
  UiSectionSecondary,
  UiContentStack,
  UiEmptyStatePanel,
  UiContentStackSecondary,
  UiPageHeading,
  UiSectionHeading,
  UiTitleHeading,
  UiTextBlock,
  UiEyebrowTextSecondary,
  UiBodyTextSecondary,
  UiBodyTextTertiary,
  UiBodyTextQuaternary,
  UiEyebrowText,
  UiBodyText,
  UiSectionTertiary,
  UiInlineTextSecondary,
} from "components/admin/styles/ui.inline.styles";
import { useSetCurrentCrumbLabel } from "context/breadcrumb-entity-label-context";

type Crumb = {
  label: string;
  to?: string;
};
export const PageHeader: React.FC<{
  title: string;
  description?: string;
  eyebrow?: string;
  actions?: React.ReactNode;
  breadcrumbLabel?: string;
  crumbs?: Crumb[];
  compact?: boolean;
}> = ({ title, description, eyebrow, actions, breadcrumbLabel, crumbs = [], compact = false }) => {
  useSetCurrentCrumbLabel(breadcrumbLabel || crumbs[crumbs.length - 1]?.label);

  return (
    <UiSectionBody $compact={compact}>
      <UiContentStack>
        {eyebrow ? <UiEyebrowText>{eyebrow}</UiEyebrowText> : null}
        <UiContentStackSecondary>
          <UiPageHeading>{title}</UiPageHeading>
          {description ? <UiBodyText>{description}</UiBodyText> : null}
        </UiContentStackSecondary>
      </UiContentStack>
      {actions ? <UiSection>{actions}</UiSection> : null}
    </UiSectionBody>
  );
};
export const SectionCard: React.FC<{
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ title, subtitle, action, children, className = "" }) => (
  <UiSectionTertiary $surfaceClassName={className}>
    {title || subtitle || action ? (
      <UiSectionHeader>
        <UiContentStackTertiary>
          {title ? <UiSectionHeading>{title}</UiSectionHeading> : null}
          {subtitle ? <UiTextBlock>{subtitle}</UiTextBlock> : null}
        </UiContentStackTertiary>
        {action}
      </UiSectionHeader>
    ) : null}
    {children}
  </UiSectionTertiary>
);
export const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  helper?: string;
}> = ({ label, value, helper }) => (
  <UiMediaSurface>
    <UiSectionSecondary />
    <UiEyebrowTextSecondary>{label}</UiEyebrowTextSecondary>
    <UiBodyTextSecondary>{value}</UiBodyTextSecondary>
    {helper ? <UiBodyTextTertiary>{helper}</UiBodyTextTertiary> : null}
  </UiMediaSurface>
);
export const StatusPill: React.FC<{
  value?: string | null;
}> = ({ value }) => {
  const normalized = String(value ?? "Unknown")
    .trim()
    .toLowerCase();
  const palette =
    normalized === "active" || normalized === "paid" || normalized === "completed"
      ? "bg-emerald-500/12 text-emerald-200 border-emerald-400/20"
      : normalized === "pending"
      ? "bg-[rgba(240,191,86,0.12)] text-[var(--brand-highlight)] border-[rgba(240,191,86,0.22)]"
      : normalized === "cancelled" || normalized === "inactive"
      ? "bg-[var(--brand-error-ring)] text-[var(--brand-error-text)] border-[var(--brand-error-border)]/20"
      : "bg-white/5 text-[var(--brand-muted)] border-white/10";
  return <UiInlineTextSecondary $palette={palette}>{value ?? "Unknown"}</UiInlineTextSecondary>;
};
export const EmptyState: React.FC<{
  title: string;
  description?: string;
}> = ({ title, description }) => (
  <UiEmptyStatePanel>
    <UiTitleHeading>{title}</UiTitleHeading>
    {description ? <UiBodyTextQuaternary>{description}</UiBodyTextQuaternary> : null}
  </UiEmptyStatePanel>
);
